import { useData } from "data/data-provider";
import { FC } from "react";

export interface SectionHeroProps {
  className?: string;
}

const SectionHero: FC<SectionHeroProps> = ({ className = "" }) => {
  const { banner, companyDetails } = useData();

  // Eyebrow tagline — comes from FMS (`banner_tagline` on
  // CompanyWebsiteDetail). Falls back to the company name when the field is
  // empty so the pill is never blank if the tenant hasn't set a tagline yet.
  const tagline: string | undefined =
    companyDetails?.banner_tagline?.trim() ||
    companyDetails?.company_name?.trim() ||
    "";

  // Banner text alignment is chosen in the FMS (Section.alignment).
  //   "left"   → text + eyebrow on the left  (legacy default)
  //   "center" → both centered
  //   "right"  → both right-aligned
  // Default to "left" so tenants who haven't set an alignment keep the
  // original layout exactly.
  const alignment: "left" | "center" | "right" =
    banner?.alignment === "center" || banner?.alignment === "right"
      ? banner.alignment
      : "left";

  // Tailwind class fragments per alignment. Kept as flat string maps (not
  // template strings) so Tailwind's JIT can statically detect every class
  // and include it in the bundle — `text-${alignment}` would silently miss.
  const textBlockAlignment: Record<typeof alignment, string> = {
    left: "text-center sm:text-left mr-auto",
    center: "text-center mx-auto",
    right: "text-center sm:text-right ml-auto",
  };
  const eyebrowJustify: Record<typeof alignment, string> = {
    left: "justify-center sm:justify-start",
    center: "justify-center",
    right: "justify-center sm:justify-end",
  };

  return (
    // `w-full` so this fills its flex parent in PageHome.tsx
    // (`<div class="flex-1 flex items-center ...">`). Without it the outer
    // div shrinks to content width and the inner `container mx-auto` has
    // nothing to center within, so the banner content left-aligns to the
    // viewport while the Header (a plain block element) stays centered —
    // making the logo look pushed right relative to the banner heading.
    <div
      className={`nc-SectionHero relative w-full ${className}`}
      data-nc-id="SectionHero"
    >
      {/* Plain `container` — relies on Tailwind's container config for the
          max-width + horizontal padding. Earlier this had an extra
          `px-4 sm:px-8 lg:px-12` on top of `container`, which pushed the
          banner content 32-48px further inset than the logo (which only
          carries 16px from its own container). Removing those overrides so
          the banner heading / eyebrow pill / description left-align with
          the header logo and every section below the fold. */}
      <div className="container py-10 sm:py-14 lg:py-20 relative">
        {/* Eyebrow tagline (was a hardcoded "Home > For Rent" breadcrumb —
            replaced with a configurable pill driven by FMS data). Justified
            per banner.alignment so the pill follows the heading. */}
        {tagline && (
          <div className={`mb-6 sm:mb-8 flex ${eyebrowJustify[alignment]}`}>
            <span className="inline-flex items-center gap-2 rounded-full bg-[#FFFFFF26] border border-primary-100 px-4 py-2 text-xs sm:text-sm font-semibold uppercase tracking-[0.18em] text-banner-description-color backdrop-blur-sm">
              <span className="h-1.5 w-1.5 rounded-full bg-primary-200" />
              {tagline}
            </span>
          </div>
        )}

        <div
          className={`max-w-3xl xl:max-w-4xl ${textBlockAlignment[alignment]}`}
        >
          {/* Clamp a long banner title to 4 lines so it truncates within the
              fixed-height hero instead of overflowing and bleeding into the
              Vehicles section below (1NOW-1264). `[&_p]:inline` flattens the
              rich-text <p> wrapper so the line-clamp counts text lines, not a
              single block. */}
          <h1 className="text-3xl sm:text-5xl md:text-6xl lg:text-6xl xl:text-7xl font-bold text-banner-heading-color leading-[1.05] tracking-tight line-clamp-4 [&_p]:m-0 [&_p]:inline">
            <span dangerouslySetInnerHTML={{ __html: banner?.heading }} />
          </h1>

          {/* Clamp a long banner description to 3 lines so it truncates with
              an ellipsis (…) inside the fixed-height hero instead of spilling
              over the image / scroll cue. The clamp lives on this wrapper (not
              the gradient span) so the element is a real `-webkit-box` with a
              visible text color — the auto-generated ellipsis inherits that
              color and actually shows. `[&_p]:inline` flattens the rich-text
              <p> wrapper so line-clamp counts text lines. */}
          <div className="mt-5 sm:mt-6 lg:mt-7 text-banner-description-color line-clamp-3 [&_p]:m-0 [&_p]:inline">
            <span
              className="text-base sm:text-lg md:text-xl lg:text-xl xl:text-2xl !text-banner-description-color font-medium leading-relaxed"
            >
              <span dangerouslySetInnerHTML={{ __html: banner?.description }} />
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SectionHero;
